/**
 * Thin wrapper around browser storage for auth tokens and user metadata.
 *
 * Centralizing access here means:
 *   - one place to migrate to httpOnly cookies later
 *   - one place to handle SSR / storage-unavailable edge cases
 *   - no magic string keys scattered through the app
 */

const TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refresh_token";
const USER_KEY = "user";
const USER_TYPE_KEY = "userType";

const safeStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const getItem = (key) => {
  const store = safeStorage();
  if (!store) return null;
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const setItem = (key, value) => {
  const store = safeStorage();
  if (!store) return;
  try {
    store.setItem(key, value);
  } catch {
    /* quota exceeded or unavailable — intentionally swallow */
  }
};

const removeItem = (key) => {
  const store = safeStorage();
  if (!store) return;
  try {
    store.removeItem(key);
  } catch {
    /* ignore */
  }
};

export const getToken = () => getItem(TOKEN_KEY);
export const setToken = (value) => setItem(TOKEN_KEY, value);

export const getRefreshToken = () => getItem(REFRESH_TOKEN_KEY);
export const setRefreshToken = (value) => setItem(REFRESH_TOKEN_KEY, value);

export const getUser = () => {
  const raw = getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
export const setUser = (user) => {
  if (user == null) {
    removeItem(USER_KEY);
    return;
  }
  setItem(USER_KEY, typeof user === "string" ? user : JSON.stringify(user));
};

export const getUserType = () => getItem(USER_TYPE_KEY);
export const setUserType = (type) => setItem(USER_TYPE_KEY, type);

export const clearAuth = () => {
  removeItem(TOKEN_KEY);
  removeItem(REFRESH_TOKEN_KEY);
  removeItem(USER_KEY);
  removeItem(USER_TYPE_KEY);
};

/** Escape hatch for keys not yet migrated to a named helper. */
export const storage = {
  get: getItem,
  set: setItem,
  remove: removeItem,
};
